import {
	alpha,
	Box,
	Button,
	FormControl,
	FormHelperText,
	Tooltip,
	Typography,
} from '@mui/material';
import config from 'config';
import React, { useState } from 'react';
import { type FieldError, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';

import { login } from '../../actions/auth.actions';
import KeycloakSVG from '../../assets/blsa.svg';
import { palette, spaces } from '../../assets/styles/themes/tokens';
import { colors } from '../../assets/styles/themes/variables';
import { LoginSources, routes } from '../../constants';
import { isFeatureEnabled } from '../../utils/feature';
import { password } from '../../validators';
import LabelledTextField from '../LabelledTextField/LabelledTextField';

interface LoginProps {
	onLoginSuccess: (source: LoginSources) => void;
	onLoginFailure: (source: LoginSources, statusCode: number, errorCode?: number) => void;
}

interface LoginCredentials {
	email: string;
	password: string;
}

const loginFormValidator = yup.object<LoginCredentials>({
	email: yup.string().email('Invalid username or password').required('This field is required'),
	password: password(),
});

const LoginForm = ({ onLoginSuccess, onLoginFailure }: LoginProps) => {
	const dispatch = useDispatch();
	const [error, setError] = useState<FieldError | undefined>(undefined);
	const { t } = useTranslation();
	const { errors, handleSubmit, register } = useForm<LoginCredentials>({
		mode: 'onBlur',
		validationSchema: loginFormValidator,
	});
	const isKeycloakSSOEnabled = isFeatureEnabled('keycloakSSO');

	const getErrorMessage = (error: Error): FieldError | undefined => {
		const knownMessages = [
			'Invalid username or password',
			'Password is expired',
			'User has not confirmed through opt-in',
		];

		const match = knownMessages.find((msg) => error.message.includes(msg));

		return match
			? {
					type: 'manual',
					message: t(match),
				}
			: undefined;
	};

	const handleOnSubmit = handleSubmit(
		// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
		React.useCallback(
			async (credentials: LoginCredentials) => {
				try {
					setError(undefined);
					await dispatch(login(credentials.email, credentials.password));
					onLoginSuccess(LoginSources.email);
				} catch (err: any) {
					err.data = err.data || {};
					onLoginFailure(LoginSources.email, err.statusCode, err.data.code);
					setError(getErrorMessage(err));
				}
			},
			[onLoginSuccess, onLoginFailure]
		)
	);

	const loginButtonIcon = (
		<Button
			data-testid="login-submit-button-icon"
			variant="outlined"
			color="primary"
			size="large"
			type={isKeycloakSSOEnabled ? undefined : 'button'}
			aria-label={t('Education Server Saxony-Anhalt')}
			startIcon={<KeycloakSVG aria-hidden="true" />}
			sx={{
				borderColor: alpha(colors.blue700, 0.48),
				width: '100%',
				'&:hover': {
					backgroundColor: palette.primary.hover,
					color: palette.primary.main,
				},
			}}
			href={isKeycloakSSOEnabled ? config.links.sso.keycloak : undefined}
			disableRipple={!isKeycloakSSOEnabled}
			disableFocusRipple={!isKeycloakSSOEnabled}
		>
			{t('Education Server Saxony-Anhalt')}
		</Button>
	);

	return (
		<Box
			sx={{
				height: 'fit-content',
			}}
		>
			<Box
				component="form"
				onSubmit={handleOnSubmit}
				data-testid="login-form"
				sx={{
					display: 'flex',
					flexDirection: 'column',
					height: '100%',
				}}
			>
				<FormControl fullWidth sx={{ gap: spaces.md }}>
					<LabelledTextField
						dataTestId="login-page-form-email-input"
						name="email"
						label={t('E-Mail-Address *')}
						placeholder={t('eg max.mustermann@beispiel.de')}
						inputRef={register}
						error={errors.email}
					/>
					<Box>
						<LabelledTextField
							dataTestId="login-page-form-password-input"
							name="password"
							label={t('Password *')}
							placeholder={t('Enter your password')}
							isPasswordTextField
							inputRef={register}
							error={errors.password}
						/>
						<Box
							sx={{
								width: 'fit-content',
								marginTop: spaces.xs,
								marginRight: '0',
								marginLeft: 'auto',
							}}
						>
							<Button
								data-testid="forgot-passsword-button"
								variant="text"
								size="small"
								color="secondary"
								type="button"
								aria-label={t('Forgot your password?')}
								sx={{
									'&:hover': {
										backgroundColor: palette.action.hover,
										color: palette.secondary.main,
									},
								}}
								href={`/${routes.resetPassword.root}`}
							>
								{t('Forgot your password?')}
							</Button>
						</Box>
					</Box>
					<FormHelperText error={!!error}>{error?.message}</FormHelperText>
					<Button
						data-testid="login-submit-button"
						variant="contained"
						size="large"
						color="primary"
						type="submit"
						aria-label={t('Login')}
						sx={{
							width: '100%',
						}}
					>
						{t('Login')}
					</Button>
				</FormControl>
				<Typography
					data-testid="or-text"
					variant="caption"
					color={palette.text.secondary}
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						marginTop: spaces.xs,
						marginBottom: spaces.xs,
					}}
				>
					{t('or')}
				</Typography>
				{isKeycloakSSOEnabled ? (
					loginButtonIcon
				) : (
					<Tooltip title={t('Education-LSA Lock Information')} placement="right">
						{loginButtonIcon}
					</Tooltip>
				)}
			</Box>
		</Box>
	);
};

export default LoginForm;
